import * as Mutations from './mutations';
import * as ROLES from '@/constants/user-types';
import { clearAllSelected } from '@/store/modules/page/reset';
import { PaginationParams } from '@/common/utils/pagination-params';
import { SchoolAftercareService } from '@/common/services/pages/aftercare.service';

export const AftercarePageModule = {
    state: {
        aftercareSessionName: null,
        aftercareList: null,
        aftercareAddOnsUsed: null,
        aftercareFreeDays: null,
        aftercareId: null,
        aftercareDayId: null,
        aftercareSessionDay: null
    },

    mutations: {
        [Mutations.SET_AFTERCARE_LIST](state, aftercareList) {
            state.aftercareList = aftercareList;
        },

        [Mutations.FETCHED_AFTERCARE_SESSION_NAME](state, aftercareSessionName) {
            state.aftercareSessionName = aftercareSessionName;
        },

        [Mutations.CLEAR_AFTERCARE_SESSION_NAME](state) {
            state.aftercareSessionName = null;
        },

        [Mutations.SET_AFTERCARE_ADDONS_USED](state, aftercareAddOnsUsed) {
            state.aftercareAddOnsUsed = aftercareAddOnsUsed;
        },

        [Mutations.CLEAR_AFTERCARE_ADDONS_USED](state) {
            state.aftercareAddOnsUsed = null;
        },

        [Mutations.SET_AFTERCARE_FREE_DAYS_ENROLL_CHILD_ROSTERS](state, aftercareFreeDays) {
            state.aftercareFreeDays = aftercareFreeDays;
        },

        [Mutations.CLEAR_AFTERCARE_FREE_DAYS_ENROLL_CHILD_ROSTERS](state) {
            state.aftercareFreeDays = null;
        },

        [Mutations.SET_AFTERCARE_ID] (state, aftercareId) {
            state.aftercareId = aftercareId;
        },

        [Mutations.CLEAR_AFTERCARE_ID] (state) {
            state.aftercareId = null;
        },

        [Mutations.SET_AFTERCARE_DAY_ID] (state, aftercareDayId) {
            state.aftercareDayId = aftercareDayId;
        },

        [Mutations.CLEAR_AFTERCARE_DAY_ID] (state) {
            state.aftercareDayId = null;
        },

        [Mutations.SET_AFTERCARE_SESSION_DAY] (state, aftercareSessionDay) {
            state.aftercareSessionDay = aftercareSessionDay;
        },

        [Mutations.CLEAR_AFTERCARE_SESSION_DAY] (state) {
            state.aftercareSessionDay = null;
        }
    },

    actions: {
        async getAftercareList(context, { schoolId } = { schoolId: context.getters.schoolId }) {
            const { userRole } = context.getters;
            const request = userRole === ROLES.SCHOOL_INSTRUCTOR
                ? params => SchoolAftercareService.getAftercareListByInstructor(context.getters.schoolId, context.getters.userId, params)
                : params => SchoolAftercareService.getAftercareList(schoolId, params);
            const { data: { content, pageable, totalPages, totalElements, numberOfElements } } =
                await request(
                    PaginationParams.createParams({
                        size: context.getters.pageSize,
                        page: context.getters.pageNumber,
                        sort: context.getters.sortBy,
                        direction: context.getters.direction,
                        query: context.getters.searchQuery,
                        filters: {
                            ...context.getters.filters
                        }
                    }));
            context.commit(Mutations.SET_AFTERCARE_LIST, content);
            context.dispatch('configPagination', {
                ...pageable,
                numberOfElements,
                totalElements,
                totalPages,
                callback: () => {
                    return context.dispatch('getAftercareList', { schoolId });
                }
            });
            if (userRole && userRole !== ROLES.SCHOOL_INSTRUCTOR) {
                context.dispatch('getAftercareGeneralInfo');
            }
        },

        async bulkUpdateAftercareSessions(context, form) {
            const { schoolId, selected } = context.getters;
            await SchoolAftercareService.bulkUpdateAftercareSessions(schoolId, selected, form);
            await context.dispatch('getAftercareList');
            context.dispatch('resetSelectedItems');
        },

        async deleteAftercare(context, aftercareId) {
            await context.dispatch('deleteAftercareSessions', [aftercareId]);
        },

        async deleteMultipleAftercare(context) {
            await context.dispatch('deleteAftercareSessions', context.getters.selected);
        },

        async deleteAftercareSessions(context, aftercareIds) {
            const { schoolId } = context.getters;
            await SchoolAftercareService.deleteMultipleAftercare(schoolId, aftercareIds);
            context.dispatch('resetSelectedItems');
            context.dispatch('resetFilter');
            if (aftercareIds.length === context.getters.aftercareList.length) {
                context.dispatch('openPreviousPage');
            }
            clearAllSelected();
            await context.dispatch('getAftercareList', { schoolId });
        },

        async getAftercareSessionNameById(context, aftercareDayId) {
            const { data } = await SchoolAftercareService.getAftercareSessionName(context.getters.schoolId, aftercareDayId);
            context.commit(Mutations.FETCHED_AFTERCARE_SESSION_NAME, data);
        },

        async getAftercareSessionDayNameById(context, aftercareDayId) {
            const { data } = await SchoolAftercareService.getAftercareSessionDay(context.getters.schoolId, aftercareDayId);
            context.commit(Mutations.SET_AFTERCARE_SESSION_DAY, data);
        },

        async fetchAddonsWhichUsesBefore(context, payload) {
            const { activityId, activityType, listType, childId } = payload;
            const { schoolId } = context.getters;
            const { data } = await SchoolAftercareService.getAftercareAddOnsWhichUsesBefore(schoolId, activityId, activityType, listType, childId);
            context.commit(Mutations.SET_AFTERCARE_ADDONS_USED, data);
        },

        clearAddonsWhichUsesBefore(context) {
            context.commit(Mutations.CLEAR_AFTERCARE_ADDONS_USED);
        },

        async getAftercareFreeDaysForRostersForSchool(context, payload) {
            const { activityId, childId } = payload;
            const { schoolId } = context.getters;
            const { data } = await SchoolAftercareService.getAftercareFreeDaysForRostersForSchool(schoolId, activityId, childId);
            context.commit(Mutations.SET_AFTERCARE_FREE_DAYS_ENROLL_CHILD_ROSTERS, data);
        },

        clearAftercareFreeDaysForRosters(context) {
            context.commit(Mutations.CLEAR_AFTERCARE_FREE_DAYS_ENROLL_CHILD_ROSTERS);
        },
        
        async getAftercareFreeDaysForWaitlist(context, payload) {
            const { activityId, childId } = payload;
            const { schoolId } = context.getters;
            const { data } = await SchoolAftercareService.getAftercareFreeDaysForWaitlist(schoolId, activityId, childId);
            context.commit(Mutations.SET_AFTERCARE_FREE_DAYS_ENROLL_CHILD_ROSTERS, data);
        },

        async getAftercareIdByDayId(context, aftercareDayId) {
            const { data } = await SchoolAftercareService.getAftercareIdByDayId(context.getters.schoolId, aftercareDayId);
            context.commit(Mutations.SET_AFTERCARE_ID, data);
        },

        removeAftercareId(context) {
            context.commit(Mutations.CLEAR_AFTERCARE_ID);
        },

        setAftercareDayId(context, aftercareDayId) {
            context.commit(Mutations.SET_AFTERCARE_DAY_ID, aftercareDayId);
        },

        removeAftercareDayId(context) {
            context.commit(Mutations.CLEAR_AFTERCARE_DAY_ID);
        },

        removeAftercareSessionDay(context) {
            context.commit(Mutations.CLEAR_AFTERCARE_SESSION_DAY);
        }
    },

    getters: {
        aftercareList: state => state.aftercareList,

        aftercareSessionName: state => state.aftercareSessionName,

        aftercareAddOnsUsed: state => state.aftercareAddOnsUsed,

        aftercareFreeDays: state => state.aftercareFreeDays,

        aftercareId: state => state.aftercareId,

        aftercareDayId: state => state.aftercareDayId,

        aftercareSessionDay: state => state.aftercareSessionDay
    }
};


// WEBPACK FOOTER //
// ./src/store/modules/aftercare-page/aftercare-page.module.js